/**
 * Stack Service - Manages API Gateway stack selection
 *
 * Architecture:
 * - Each developer has their own CloudFormation stack with a unique API Gateway
 * - api-dev.rocketpricing.com uses base path mappings to route to different stacks:
 *   - /sbass → frcukt079h (sbass's stack)
 *   - /jbailey → 5ccn4xlj00 (jbailey's stack)
 *   - /dev → hbsluz61sc (dev/shared stack)
 * - Frontend allows users to select which stack to hit for testing
 * - User's email determines default stack selection
 *
 * IMPORTANT: Stack selection ONLY applies to development environment.
 * Stage and production environments use their API URLs directly without base paths.
 */

import config from '../config/environments'

export type StackName = 'sbass' | 'jbailey' | 'pyasi' | 'dev';

interface StackConfig {
  name: StackName;
  displayName: string;
  apiGatewayId: string;
  basePath: string;
}

const STACK_CONFIGS: StackConfig[] = [
  {
    name: 'sbass',
    displayName: 'SBass Stack',
    apiGatewayId: 'frcukt079h',
    basePath: '/sbass'
  },
  {
    name: 'jbailey',
    displayName: 'JBailey Stack',
    apiGatewayId: '5ccn4xlj00',
    basePath: '/jbailey'
  },
  {
    name: 'pyasi',
    displayName: 'PYasi Stack',
    apiGatewayId: '9m57fzt9te',
    basePath: '/pyasi'
  },
  {
    name: 'dev',
    displayName: 'Dev (Shared)',
    apiGatewayId: 'hbsluz61sc',
    basePath: '/dev'
  }
];

const STORAGE_KEY = 'selectedStack';

/**
 * Get default stack based on user email
 */
export const getDefaultStackForUser = (email: string | null): StackName => {
  if (!email) return 'dev';

  const emailLower = email.toLowerCase();

  // Map email to stack owner
  if (emailLower.includes('sbass@')) return 'sbass';
  if (emailLower.includes('jbailey@')) return 'jbailey';
  if (emailLower.includes('paul@')) return 'pyasi';

  // Default to dev/shared stack
  return 'dev';
};

/**
 * Get all available stacks
 */
export const getAllStacks = (): StackConfig[] => {
  return STACK_CONFIGS;
};

/**
 * Get configuration for a specific stack
 */
export const getStackConfig = (stackName: StackName): StackConfig => {
  const config = STACK_CONFIGS.find(s => s.name === stackName);
  if (!config) {
    throw new Error(`Invalid stack name: ${stackName}`);
  }
  return config;
};

/**
 * Get currently selected stack from localStorage
 */
export const getSelectedStack = (): StackName => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && STACK_CONFIGS.find(s => s.name === stored)) {
    return stored as StackName;
  }

  // Default to dev if nothing stored
  return 'dev';
};

/**
 * Set selected stack in localStorage
 */
export const setSelectedStack = (stackName: StackName): void => {
  localStorage.setItem(STORAGE_KEY, stackName);

  // Emit custom event so other components can react
  window.dispatchEvent(new CustomEvent('stack-changed', { detail: stackName }));
};

/**
 * Get API base URL for currently selected stack
 *
 * IMPORTANT: Stack selection only applies to development environment.
 * Stage and production use their API URLs directly without base paths.
 */
export const getApiBaseUrl = (baseUrl: string): string => {
  // Remove trailing slash from base URL if present
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');

  // Only apply stack base paths in development environment
  // Stage and production environments use API URL directly
  if (config.env !== 'development') {
    return cleanBaseUrl;
  }

  const selectedStack = getSelectedStack();
  const stackConfig = getStackConfig(selectedStack);

  // Add base path for development
  return `${cleanBaseUrl}${stackConfig.basePath}`;
};

/**
 * Initialize stack selection based on user email
 * Call this after user login
 */
export const initializeStackForUser = (email: string | null): void => {
  // Only set default if no stack is currently selected
  const current = localStorage.getItem(STORAGE_KEY);
  if (!current) {
    const defaultStack = getDefaultStackForUser(email);
    setSelectedStack(defaultStack);
  }
};

/**
 * Stack Service API
 */
export const stackService = {
  getAllStacks,
  getStackConfig,
  getSelectedStack,
  setSelectedStack,
  getApiBaseUrl,
  getDefaultStackForUser,
  initializeStackForUser
};

export default stackService;
